import type { DraftState, WizardQuestion, WizardSampleGroup, WizardStep } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { FileForProcessing } from '../../Model/Sample/ISample.ts'
import type { ValidationIssue } from '../../Model/Order/ValidationIssue.ts'
import { FAILED_WRITES, reviewWarnings } from './reviewWarnings.ts'
import { groupsWithoutPrimers, primersMissingSubject } from './primerCheck.ts'
import { isStillWriting } from './writeState.ts'
import { reviewSignature } from './reviewSignature.ts'
import { countItems, sampleDisplayName, sampleNameOf } from './sampleLabels.ts'
import { isArchiveOrder } from './archive.ts'
import { currentPools, lacksControl, placedGroups, poolContents, poolNames } from './pools.ts'
import { poolCopy } from './wizardSettings.ts'
import { failedRemovals } from './removal.ts'
import { controlsAsked, poolSuggestionPending, QUESTION_STEP } from './questions.ts'
import { currentTargetCheck } from './targetCheck.ts'
import { groupsMissingReverse, openHalvesHint } from './reverseFiles.ts'
import { taxonLabel } from '../../View/Page/OrderWizard/wizardLabels.ts'
import { plural } from '../../Util/plural.ts'
import { lengthWarning } from './lengthCheck.ts'

export type IssueSeverity = 'blocker' | 'warning'

export type WizardIssue = {
  id: string
  severity: IssueSeverity
  step: WizardStep
  title: string
  groupKeys?: string[]
  /** The DOM id to scroll to and highlight: a question card or a sample row. */
  target?: string
  /** The affected samples by name, for the expanded view. */
  details?: string[]
}

/** Worth knowing but not a problem: goes into the step's summary line. */
export type InfoNote = { step: WizardStep; text: string }

export type IssueList = { blockers: WizardIssue[]; warnings: WizardIssue[]; infos: InfoNote[] }

/** A warning's identity including its content: acknowledging it holds until the cause changes. */
export const acknowledgementKey = (issue: WizardIssue) => `${issue.id}|${(issue.groupKeys ?? []).join(',')}|${issue.title}`

export type IssueContext = {
  /** Whether a group's files can still be read in this browser session. */
  filesAtHand: (group: WizardSampleGroup) => boolean
}

export const STEPS: WizardStep[] = ['files', 'pools', 'primers', 'metadata', 'review']

export const STEP_LABELS: Record<WizardStep, string> = {
  files: 'Files',
  pools: 'Samples & controls',
  primers: 'Primers',
  metadata: 'Metadata',
  review: 'Review & submit'
}

export const questionTarget = (id: string) => `wizard-question-${id}`
export const groupTarget = (key: string) => `wizard-group-${encodeURIComponent(key)}`
/** 0-based pool. */
export const poolTarget = (pool: number) => `wizard-pool-${pool + 1}`
export const REMOVAL_FAILED_TARGET = 'wizard-removal-failed'
export const FILE_LIST_TARGET = 'wizard-file-list'
export const SHEET_READD_TARGET = 'wizard-sheet-readd'
export const ORDER_SETTINGS_TARGET = 'wizard-order-settings'

/** A server issue's step: sample shape and pools under samples & controls, the rest (files, platform, settings) under files. */
const SERVER_ISSUE_STEP: Partial<Record<ValidationIssue['type'], WizardStep>> = {
  'empty-pool': 'pools',
  'sample-invalid': 'pools',
  'invalid-multi-sample': 'pools',
  'invalid-merged-sample': 'pools',
  'invalid-trimmed-sample': 'pools',
  'sample-not-merged': 'pools',
  'sample-not-trimmed': 'pools',
  'order-already-submitted': 'review',
  'no-changed-samples': 'review'
}

const groupOfItem = (draft: DraftState, item: FileForProcessing | null | undefined): WizardSampleGroup | undefined =>
  item
    ? Object.values(draft.groups).find(
        (group) => group.item.itemId === item.id || draft.files[group.forward]?.upload?.serverFileId === item.forwardFileId
      )
    : undefined

/** A server-side validation issue as a blocker in the wizard's list, placed on the step that fixes it. */
export const serverIssue = (draft: DraftState, issue: ValidationIssue, index: number, source: 'platform' | 'submit'): WizardIssue => {
  const text = 'issue' in issue && typeof issue.issue === 'string' && issue.issue ? issue.issue : issue.type.replaceAll('-', ' ')
  const item = 'item' in issue ? (issue.item as FileForProcessing | null | undefined) : undefined
  const group = groupOfItem(draft, item)
  const sample = group ? sampleDisplayName(draft, group.key) : item?.customerSampleName
  const missing = issue.type === 'missing-file' ? (issue.originalFileName ?? undefined) : undefined
  return {
    id: `server-${source}-${index}-${issue.type}`,
    severity: 'blocker',
    step: SERVER_ISSUE_STEP[issue.type] ?? 'files',
    title: [sample, missing, text].filter(Boolean).join(': '),
    ...(group ? { groupKeys: [group.key], target: groupTarget(group.key) } : {})
  }
}

const REFUSAL_STEP: Record<string, WizardStep> = {
  'not-lab': 'review',
  platform: 'files',
  'amplicon-mismatch': 'primers',
  'primer-not-found': 'primers',
  'primer-ambiguous': 'review',
  validation: 'primers'
}

const uploadIssues = (draft: DraftState): WizardIssue[] => {
  const files = Object.values(draft.files)
  const broken = files.filter((file) => file.upload?.status === 'failed' || file.upload?.status === 'needs-readd')
  const rejected = files.filter((file) => file.kind === 'fastq' && !!file.rejection)
  return [
    ...(broken.length > 0
      ? [
          {
            id: 'uploads-broken',
            severity: 'blocker' as const,
            step: 'files' as const,
            title: `${plural(broken.length, 'file')} not uploaded — retry or drop again`,
            target: FILE_LIST_TARGET
          }
        ]
      : []),
    ...(rejected.length > 0
      ? [
          {
            id: 'files-rejected',
            severity: 'warning' as const,
            step: 'files' as const,
            title: `${plural(rejected.length, 'file')} not taken into the order`,
            target: FILE_LIST_TARGET
          }
        ]
      : [])
  ]
}

export const REVERSE_MISSING = 'reverse-missing'

/* a paired-end order's sample needs both files; the R2 dropped later joins it */
const reverseIssues = (draft: DraftState): WizardIssue[] => {
  const missing = groupsMissingReverse(draft)
  return missing.length > 0
    ? [
        {
          id: REVERSE_MISSING,
          severity: 'blocker',
          step: 'files',
          title: `${countItems(missing)} ${missing.length === 1 ? 'is' : 'are'} missing ${missing.length === 1 ? 'its' : 'their'} R2 file — drop the R2 files`,
          groupKeys: missing.map((group) => group.key),
          target: FILE_LIST_TARGET,
          details: missing.map((group) => sampleDisplayName(draft, group.key))
        }
      ]
    : []
}

/* the order still holds these items, so it differs from what the lab sees */
const removalIssues = (draft: DraftState): WizardIssue[] => {
  const failed = failedRemovals(draft)
  return failed.length > 0
    ? [
        {
          id: 'removals-failed',
          severity: 'blocker',
          step: 'pools',
          title: `${plural(failed.length, 'item')} could not be removed from the order — try again`,
          target: REMOVAL_FAILED_TARGET,
          details: failed.map((removal) => removal.name)
        }
      ]
    : []
}

/* the wizard removes other empty pools itself; these it leaves to the lab */
const emptyPools = (draft: DraftState): WizardIssue[] => {
  const groups = placedGroups(draft)
  if (groups.length === 0 || groups.some((group) => group.item.status !== 'created')) {
    return []
  }
  const used = new Set(groups.map((group) => group.item.poolId ?? draft.poolIds[0]))
  const stuck = draft.poolIds.filter((id, pool) => !!id && !used.has(id) && (pool === 0 || (draft.stuckPools ?? []).includes(id)))
  return stuck.length > 0
    ? [
        {
          id: 'pools-empty',
          severity: 'blocker',
          step: 'pools',
          title: `${plural(stuck.length, 'pool')} without samples that the wizard cannot remove — assign samples to it, or remove it in the classic view`
        }
      ]
    : []
}

const duplicateNames = (draft: DraftState): WizardIssue[] => {
  const byName = new Map<string, string[]>()
  for (const group of placedGroups(draft)) {
    const name = sampleNameOf(draft, group)
    if (name) {
      byName.set(name, [...(byName.get(name) ?? []), group.key])
    }
  }
  const duplicated = [...byName].filter(([, keys]) => keys.length > 1)
  if (duplicated.length === 0) {
    return []
  }
  const keys = duplicated.flatMap(([, groupKeys]) => groupKeys)
  return [
    {
      id: 'names-duplicate',
      severity: 'warning',
      step: 'pools',
      title: `${plural(duplicated.length, 'name')} used by more than one sample`,
      groupKeys: keys,
      target: groupTarget(keys[0]),
      details: duplicated.map(([name, groupKeys]) => `${name} (${groupKeys.length}×)`)
    }
  ]
}

/** An open question holds the lab up when it blocks creating the order, when items lack primers, or an archive its sheet. */
export const questionSeverity = (question: WizardQuestion): IssueSeverity =>
  question.blocking || question.kind === 'primers-missing' || question.kind === 'archive-needs-sheet' ? 'blocker' : 'warning'

export const ARCHIVE_NEEDS_SHEET = 'This archive needs a sample sheet that lists its samples and their primers'

const questionIssues = (draft: DraftState): WizardIssue[] =>
  draft.questions.map((question) => {
    const step = QUESTION_STEP[question.kind]
    const severity = questionSeverity(question)
    const titles: Record<WizardQuestion['kind'], string> = {
      'processing-type-conflict': 'Choose the processing type',
      'format-multiplex': 'One sample per file pair, or several?',
      'sheet-key-column': 'The sheet has no column that names the files or samples',
      'controls-none-found': 'No negative controls found',
      'controls-too-many': 'Most items look like negative controls',
      pools: 'Pools not confirmed',
      'primers-missing': 'Items without primers',
      'archive-needs-sheet': ARCHIVE_NEEDS_SHEET,
      'read-layout': 'Are the mate files missing, or are these merged reads?',
      'primer-trimming': 'Are the primers still on the reads?',
      'target-taxon': 'Choose the target taxon',
      'target-conflict': 'The reads and the sheet’s primers name different targets',
      'target-reads-conflict': 'The R2 reads’ primers name another target'
    }
    const groupKeys = question.kind === 'primers-missing' ? question.groupKeys : undefined
    const title =
      question.kind === 'primers-missing'
        ? `${primersMissingSubject(question)} without primers`
        : titles[question.kind]
    return {
      id: `question-${question.id}`,
      severity,
      step,
      title,
      target: questionTarget(question.id),
      ...(groupKeys ? { groupKeys } : {})
    }
  })

/** "Pool 2 holds only negative controls: move a sample into it, or its controls to a pool with samples". */
export const onlyControlsText = (pools: readonly number[]): string => {
  const names = poolNames(pools)
  return `${names.charAt(0).toUpperCase()}${names.slice(1)} ${pools.length === 1 ? 'holds' : 'hold'} only negative controls: move a sample in, or the controls to a pool with samples`
}

/**
 * Pools without a negative control and pools of controls only (warnings), on the pools as they
 * are now. Nothing while a negative-control question is open, or when the lab said the order has none:
 * that question already covers an order without controls.
 */
const poolFindings = (draft: DraftState): { issues: WizardIssue[]; infos: InfoNote[] } => {
  const none = { issues: [], infos: [] }
  if (isArchiveOrder(draft)) {
    return none
  }
  const pools = poolContents(draft, currentPools(draft))
  const controls = pools.reduce((sum, pool) => sum + pool.controls.length, 0)
  if (pools.length === 0 || controlsAsked(draft) || (controls === 0 && draft.controlsAnswer === 'none')) {
    return none
  }
  const without = pools.filter(lacksControl).map((pool) => pool.pool)
  const onlyControls = pools.length > 1 ? pools.filter((pool) => pool.samples === 0).map((pool) => pool.pool) : []
  const issues: WizardIssue[] =
    without.length === 0
      ? []
      : [
          {
            id: 'pools-without-control',
            severity: 'warning',
            step: 'pools',
            title: poolCopy().withoutControl(pools.length === 1 ? 'the pool' : poolNames(without), without.length > 1),
            target: poolTarget(without[0])
          }
        ]
  /* a pool's samples are what its controls filter, and the primers are checked on the first pool's first sample */
  const onlyControlsIssues: WizardIssue[] =
    onlyControls.length === 0
      ? []
      : [
          {
            id: 'pools-only-controls',
            severity: 'warning',
            step: 'pools',
            title: onlyControlsText(onlyControls),
            target: poolTarget(onlyControls[0])
          }
        ]
  return { issues: [...issues, ...onlyControlsIssues], infos: [] }
}

export const READ_LENGTH_ISSUE = 'read-length'

/** The review's read-length warning while the review is of the draft as it is: on Primers when a primer pair sets the window. */
const readLengthIssues = (draft: DraftState): WizardIssue[] => {
  const flow = draft.submitFlow
  const review = flow && flow.stage !== 'checking' && flow.signature === reviewSignature(draft) ? flow.review : undefined
  const title = review?.lengthCheck && lengthWarning(review.lengthCheck, review.primer)
  if (!review || !title) {
    return []
  }
  return [{ id: READ_LENGTH_ISSUE, severity: 'warning', step: review.primer === 'SKIPPED' ? 'review' : 'primers', title }]
}

/**
 * Blockers: what would make submit fail or the order differ from what the lab sees; warnings: what the lab
 * may accept knowingly; each on the step that fixes it. Submit checks count only while they ran against
 * the draft as it is.
 */
export const collectIssues = (draft: DraftState, context: IssueContext): IssueList => {
  const issues: WizardIssue[] = []
  if (draft.error) {
    issues.push({ id: 'draft-error', severity: 'blocker', step: 'files', title: draft.error })
  }
  issues.push(...uploadIssues(draft))
  issues.push(...reverseIssues(draft))
  if (draft.orderId && draft.phase === 'active' && !draft.poolIds[0]) {
    issues.push({
      id: 'pool-missing',
      severity: 'blocker',
      step: 'files',
      title: 'The order’s first pool is gone, so no file can be uploaded and no item created — continue in the classic view'
    })
  }
  if (draft.settingsWrite?.status === 'failed') {
    issues.push({
      id: 'settings-failed',
      severity: 'blocker',
      step: 'files',
      title: `The order settings could not be saved — try again${draft.settingsWrite.error ? ` (${draft.settingsWrite.error})` : ''}`,
      target: ORDER_SETTINGS_TARGET
    })
  }
  issues.push(...questionIssues(draft))
  const targetCheck = currentTargetCheck(draft)
  /* the target-conflict card already asks about the sheet's primers contradicting the target */
  const conflictAsked = draft.questions.some((question) => question.kind === 'target-conflict' || question.kind === 'target-reads-conflict')
  if (targetCheck && !conflictAsked) {
    issues.push({
      id: 'target-contradicts-primers',
      severity: 'blocker',
      step: 'files',
      title: `The primers ${targetCheck.forward} / ${targetCheck.reverse} are registered for ${targetCheck.registeredFor.map(taxonLabel).join(', ')}, not ${taxonLabel(targetCheck.target)}: submit would refuse them`,
      target: ORDER_SETTINGS_TARGET
    })
  }

  const primerQuestion = draft.questions.some((question) => question.kind === 'primers-missing')
  const withoutPrimers = primerQuestion ? [] : groupsWithoutPrimers(draft)
  if (withoutPrimers.length > 0 && !isStillWriting(draft, context.filesAtHand)) {
    issues.push({
      id: 'primers-missing',
      severity: 'blocker',
      step: 'primers',
      title: `${countItems(withoutPrimers)} without primers`,
      groupKeys: withoutPrimers.map((group) => group.key),
      target: groupTarget(withoutPrimers[0].key)
    })
  }

  const infos: InfoNote[] = []
  if (openHalvesHint(draft)) {
    infos.push({ step: 'files', text: 'R1 halves? See the hint above' })
  }
  for (const finding of reviewWarnings(draft)) {
    if (finding.level === 'info' && !finding.failure) {
      infos.push({ step: finding.topic, text: finding.text })
      continue
    }
    issues.push({
      id: finding.id,
      severity: finding.failure ? 'blocker' : 'warning',
      step: finding.topic,
      title: finding.text,
      ...(finding.groupKeys ? { groupKeys: finding.groupKeys, target: groupTarget(finding.groupKeys[0]) } : {}),
      ...(finding.details ? { details: finding.details } : {})
    })
  }
  if (draft.sheetRef && !draft.sheet) {
    issues.push({
      id: 'sheet-readd',
      severity: 'warning',
      step: 'metadata',
      title: `Drop the sample sheet ${draft.sheetRef.name} again`,
      target: SHEET_READD_TARGET
    })
  }
  issues.push(...readLengthIssues(draft))
  issues.push(...removalIssues(draft))
  issues.push(...emptyPools(draft))
  issues.push(...duplicateNames(draft))
  const pools = poolFindings(draft)
  issues.push(...pools.issues)
  infos.push(...pools.infos)

  const checks = draft.checks && draft.checks.signature === reviewSignature(draft) ? draft.checks : undefined
  /* these questions name the cause of what the primer check would refuse */
  const refusalExplained = draft.questions.some(
    (question) =>
      question.kind === 'archive-needs-sheet' ||
      question.kind === 'target-taxon' ||
      question.kind === 'target-conflict' ||
      question.kind === 'target-reads-conflict'
  )
  const refusalShown = refusalExplained || (!!targetCheck && checks?.refusal?.reason === 'amplicon-mismatch')
  if (checks?.refusal && withoutPrimers.length === 0 && !primerQuestion && !refusalShown) {
    issues.push({
      id: `refusal-${checks.refusal.reason}`,
      severity: 'blocker',
      step: REFUSAL_STEP[checks.refusal.reason] ?? 'review',
      title: checks.refusal.message
    })
  }
  ;(checks?.platformIssues ?? []).forEach((issue, index) => issues.push(serverIssue(draft, issue, index, 'platform')))
  ;(checks?.submitIssues ?? []).forEach((issue, index) => issues.push(serverIssue(draft, issue, index, 'submit')))
  ;(checks?.submitMessages ?? []).forEach((message, index) =>
    issues.push({ id: `submit-message-${index}`, severity: 'blocker', step: 'review', title: message })
  )

  const acknowledged = new Set(draft.acknowledged ?? [])
  const warnings = issues.filter((issue) => issue.severity === 'warning')
  const open = warnings.filter((issue) => !acknowledged.has(acknowledgementKey(issue)))
  for (const step of STEPS) {
    const count = warnings.filter((issue) => issue.step === step).length - open.filter((issue) => issue.step === step).length
    if (count > 0) {
      infos.push({ step, text: `${plural(count, 'warning')} acknowledged` })
    }
  }
  return { blockers: issues.filter((issue) => issue.severity === 'blocker'), warnings: open, infos }
}

export type StepState = { step: WizardStep; blockers: number; warnings: number; state: 'ok' | 'check' | 'fix' }

export const stepStates = (issues: Pick<IssueList, 'blockers' | 'warnings'>, steps: readonly WizardStep[] = STEPS): StepState[] =>
  steps.map((step) => {
    const blockers = issues.blockers.filter((issue) => issue.step === step).length
    const warnings = issues.warnings.filter((issue) => issue.step === step).length
    return { step, blockers, warnings, state: blockers > 0 ? 'fix' : warnings > 0 ? 'check' : 'ok' }
  })

/** The steps this draft shows: metadata only with a sample sheet. */
export const visibleSteps = (draft: DraftState): WizardStep[] =>
  STEPS.filter((step) => step !== 'metadata' || !!draft.sheet || !!draft.sheetRef)

/** Where to open once detection has settled: the first step with something to fix, else to check, else the start. */
export const firstAttentionStep = (issues: Pick<IssueList, 'blockers' | 'warnings'>, steps: readonly WizardStep[]): WizardStep => {
  const states = stepStates(issues, steps).filter((state) => state.step !== 'review')
  return (states.find((state) => state.state === 'fix') ?? states.find((state) => state.state === 'check'))?.step ?? steps[0]
}

/** Whether the lab may go on from a step: not past something to fix; warnings may be passed knowingly. */
export const nextAllowed = (issues: Pick<IssueList, 'blockers' | 'warnings'>, step: WizardStep): { allowed: boolean; anyway: boolean } => {
  const state = stepStates(issues, [step])[0]
  return { allowed: state.state !== 'fix', anyway: state.state === 'check' }
}

/** Whether every step before Review is fine as it is, so the lab may go straight there. */
export const readyForReview = (issues: Pick<IssueList, 'blockers' | 'warnings'>, steps: readonly WizardStep[]): boolean => {
  const before = steps.filter((step) => step !== 'review')
  return stepStates(issues, before).every((state) => state.state === 'ok')
}

/** The step the lab sees: the stored one while it is shown, else the first. */
export const currentStep = (draft: DraftState, steps: readonly WizardStep[]): WizardStep =>
  draft.step && steps.includes(draft.step) ? draft.step : steps[0]

export type StepNavigation = {
  /** Where Next goes: the following step, or Review when every step before it is fine. */
  next: WizardStep | undefined
  previous: WizardStep | undefined
  /** Whether Next skips straight to Review. */
  shortcut: boolean
  allowed: boolean
  anyway: boolean
}

export const stepNavigation = (
  issues: Pick<IssueList, 'blockers' | 'warnings'>,
  steps: readonly WizardStep[],
  step: WizardStep
): StepNavigation => {
  const index = steps.indexOf(step)
  const shortcut = step !== 'review' && steps.includes('review') && readyForReview(issues, steps)
  return {
    next: shortcut ? 'review' : steps[index + 1],
    previous: index > 0 ? steps[index - 1] : undefined,
    shortcut,
    ...nextAllowed(issues, step)
  }
}

/** What going on anyway acknowledges: the step's open warnings. */
export const warningKeysOf = (issues: Pick<IssueList, 'warnings'>, step: WizardStep): string[] =>
  issues.warnings.filter((issue) => issue.step === step).map(acknowledgementKey)

/** The issue "show me" goes to: the first blocker, else the first warning. */
export const firstIssue = (issues: Pick<IssueList, 'blockers' | 'warnings'>): WizardIssue | undefined =>
  issues.blockers[0] ?? issues.warnings[0]

/**
 * Issues the step body states in its own place: the pool headers, the sheet's re-add notice and its
 * unreadable coordinates, and the failed writes and removals, each listed there with its retry.
 */
const SHOWN_IN_BODY = new Set(['pools-without-control', 'pools-only-controls', 'sheet-readd', 'coordinates', FAILED_WRITES, 'removals-failed'])

/**
 * A step's issues that need a line of their own, blockers first: those a question card answers, or the
 * step body already states, are left to that card or body so that each shows once.
 */
export const listedIssues = (issues: Pick<IssueList, 'blockers' | 'warnings'>, step: WizardStep): WizardIssue[] =>
  [...issues.blockers, ...issues.warnings].filter(
    (issue) => issue.step === step && !issue.id.startsWith('question-') && !SHOWN_IN_BODY.has(issue.id)
  )

/** The items a step's blockers and warnings name. */
export const flaggedGroups = (issues: Pick<IssueList, 'blockers' | 'warnings'>, step: WizardStep): ReadonlySet<string> =>
  new Set([...issues.blockers, ...issues.warnings].filter((issue) => issue.step === step).flatMap((issue) => issue.groupKeys ?? []))

/** Why submit is not possible yet, or `undefined` when it is. */
export const submitBlockedReason = (issues: Pick<IssueList, 'blockers' | 'warnings'>, writing: boolean): string | undefined => {
  if (issues.blockers.length > 0) {
    return `Fix ${plural(issues.blockers.length, 'issue')} first`
  }
  if (writing) {
    return 'Still uploading and writing into the order…'
  }
  return undefined
}

/**
 * Detection has had its say: settings are planned, the order exists, and the per-sample reads (estimates,
 * runs for suggesting pools) are in or cannot come. Only then is the first step needing attention known.
 */
export const detectionSettled = (draft: DraftState): boolean =>
  !!draft.discovery &&
  !!draft.orderId &&
  draft.phase === 'active' &&
  (draft.discovery.primers.status !== 'found' || draft.restored || placedGroups(draft).every((group) => group.estimate !== undefined)) &&
  !poolSuggestionPending(draft)
